import type { DefLookup } from "../catalog";
import { PARALLAX_DC_BOARD_ID, parallaxDcBoardLimits } from "../catalog/parallax";
import { makeSystemCableSpecGetter } from "../cable-spec";
import { fitsBoardWireRange } from "../fuse-board-limits";
import { resolveProtectionDevices } from "../protection-devices";
import type { Issue, System } from "../types";

/** Published fuse ceilings are independent of contact ratings. The board's
 * common rail can receive power at several lugs or slots; a per-cable maximum
 * does not establish its aggregate current. Never sum fuse ratings as loads. */
export function runFuseBoardDiagnostics(system: System, lookup: DefLookup, targetPct: number): Issue[] {
  const boards = system.nodes.filter(node => node.kind === "component" &&
    lookup(node.definitionId, node.customDefinition)?.id === PARALLAX_DC_BOARD_ID);
  if (!boards.length) return [];
  const issues: Issue[] = [];
  const specOf = makeSystemCableSpecGetter(system, lookup, undefined, targetPct);
  const protection = resolveProtectionDevices(system, lookup);
  for (const node of boards) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition)!;
    const attached = system.cables.filter(c => c.fromNodeId === node.id || c.toNodeId === node.id);
    const label = node.label ?? def.shortName ?? def.name;
    const limits = parallaxDcBoardLimits(def);
    if (!limits) {
      issues.push({ code: "fuse-board-model-unverified", severity: "warning", nodeIds: [node.id], cableIds: attached.map(c => c.id),
        title: { key: "diag.fuse-board-model-unverified.title", vars: { label } },
        message: { key: "diag.fuse-board-model-unverified.message" },
        fix: { key: "diag.fuse-board-model-unverified.fix" } });
      continue;
    }
    if (!attached.length) continue;
    issues.push({ code: "fuse-board-total-unverified", severity: "warning", nodeIds: [node.id], cableIds: attached.map(c => c.id),
      title: { key: "diag.fuse-board-total-unverified.title", vars: { label } },
      message: { key: "diag.fuse-board-total-unverified.message", vars: { max: limits.maxTotalA } },
      fix: { key: "diag.fuse-board-total-unverified.fix" } });
    for (const cable of attached) {
      const ends = [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]];
      for (const [nodeId, terminalId] of ends) {
        const range = nodeId === node.id ? limits.wireAwgRange[terminalId] : undefined;
        if (range && !fitsBoardWireRange(specOf(cable).wire.mm2, range)) {
          const vars = { label, terminal: def.terminals.find(t => t.id === terminalId)!.label,
            smallest: range.smallest, largest: range.largest, mm2: Number(specOf(cable).wire.mm2.toFixed(2)) };
          issues.push({ code: "fuse-board-wire-fit", severity: "warning", nodeIds: [node.id], cableIds: [cable.id],
            title: { key: "diag.fuse-board-wire-fit.title", vars },
            message: { key: "diag.fuse-board-wire-fit.message", vars },
            fix: { key: "diag.fuse-board-wire-fit.fix" } });
        }
        const maximum = nodeId === node.id ? limits.slotFuseMaxA[terminalId] : undefined;
        if (maximum == null) continue;
        // A different inline family or an attached holder/post is not evidence
        // that this physical ATC slot contains the same fuse.
        const slot = protection.find(device => device.id === `slot:${node.id}:${terminalId}` && device.cableIds.includes(cable.id));
        const declared = slot ? cable.fuseAmpOverride : undefined;
        const excessive = declared != null && declared > maximum;
        if (!excessive && specOf(cable).fuseRecommendationUnavailableReason !== "equipment-slot-limit") continue;
        const vars = { label, slot: def.terminals.find(t => t.id === terminalId)!.label, max: maximum, fuse: declared ?? 0 };
        issues.push({ code: "fuse-board-slot-limit", severity: "warning", nodeIds: [node.id], cableIds: [cable.id],
          title: { key: "diag.fuse-board-slot-limit.title", vars },
          message: { key: excessive ? "diag.fuse-board-slot-limit.message" : "diag.fuse-board-slot-limit.message_sizing", vars },
          fix: { key: "diag.fuse-board-slot-limit.fix" } });
      }
    }
  }
  return issues;
}
